import axios from "axios";
import { jwtDecode } from "jwt-decode";
import accessToken from "./jwt-token-access/accessToken";
import refreshToken from "./jwt-token-access/refreshToken";

const API_URL = process.env.REACT_APP_API_URL;

const axiosApi = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const refreshInstance = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

refreshInstance.interceptors.response.use(
  (response) => {
    processQueue(null, response.data.token);
    return response;
  },
  (error) => {
    processQueue(error, null);
    return Promise.reject(error);
  }
);

axiosApi.interceptors.request.use(
  async (config) => {
    const token = accessToken();

    if (token) {
      config.headers["authorization"] = token;
    }

    config.data = { ...config.data };
    return config;
  },
  (error) => Promise.reject(error)
);

axiosApi.interceptors.response.use(
  (response) => {
    console.log("Resonse in interceptor:: ", response);
    return response
  },
  async (error) => {
    try {
      console.log("Error in Interceptor:: ", error)
      const { status } = error.response;
      const originalRequest = error.config;
      const refToken = refreshToken();
      const acc = accessToken();

      let name = null;
      let user = null;

      if (acc) {
        user = jwtDecode(acc);
        name = user["email"];
      }

      if (
        status === 401 &&
        !originalRequest._retry &&
        refToken &&
        refToken !== ""
      ) {
        console.log("401****")
        if (isRefreshing) {
          try {
            const token = await new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            });

            originalRequest.headers["authorization"] = token;
            return axiosApi(originalRequest);
          } catch (error) {
            return Promise.reject(error);
          }
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const response = await refreshInstance.post("/refresh", {
            refreshToken: refToken,
            username: name,
          });

          localStorage.setItem("authUser", JSON.stringify(response.data));
          originalRequest.headers["Authorization"] = `${response.data.token}`;
          return axiosApi(originalRequest);
        } catch (error) {
          processQueue(error, null);
          window.location.href = "/sessionExpired";
          return Promise.reject(error);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

export function setupInterceptors(history) {
  axiosApi.interceptors.response.use(
    (res) => {
      return res;
    },
    (err) => {
      try {
        const { status } = err.response;
        const originalRequest = err.config;
        const refToken = refreshToken();
        const acc = accessToken();

        let name = null;
        let user = null;

        if (acc) {
          user = jwtDecode(acc);
          name = user["email"];
        }

        if (
          status === 401 &&
          !originalRequest._retry &&
          refToken &&
          refToken !== ""
        ) {
          if (isRefreshing) {
            return new Promise(function (resolve, reject) {
              failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers["authorization"] = token;
                return axiosApi(originalRequest);
              })
              .catch((error) => {
                return Promise.reject(error);
              });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          return new Promise(function (resolve, reject) {
            refreshInstance
              .post("/refresh", {
                refreshToken: refToken,
                username: name,
              })
              .then((response) => {
                localStorage.setItem("authUser", JSON.stringify(response.data));
                processQueue(null, response.data.token);
                originalRequest.headers[
                  "authorization"
                ] = `${response.data.token}`;
                resolve(axiosApi(originalRequest));
              })
              .catch((error) => {
                processQueue(error, null);
                window.location.href = "/sessionExpired";
                reject(error);
              })
              .finally(() => {
                isRefreshing = false;
              });
          });
        }

        return Promise.reject(err);
      } catch (error) {
        return Promise.reject(err);
      }
    }
  );
}

export async function get(url, config = {}) {
  return await axiosApi
    .get(url, { ...config })
    .then((response) => ({ ...response.data, status: response.status }))
    .catch((err) => err);
}

export async function getBodyOnly(url, config = {}) {
  return await axiosApi
    .get(url, { ...config })
    .then((response) => ({ ...response.data }))
    .catch((err) => err);
}

export async function post(url, data, config = {}) {
  return axiosApi
    .post(url, { ...data }, { ...config })
    .then((response) => {
      let responseData = response.data;
      // Check if response.data is a string or an array
      if (typeof responseData === "string" || Array.isArray(responseData)) {
        return { data: responseData, status: response.status };
      }
      return { ...responseData, status: response.status };
    })
    .catch((err) => err.response);
}

export async function put(url, data, config = {}) {
  return axiosApi
    .put(url, { ...data }, { ...config })
    .then((response) => {
      console.log("put api response: ", response);
      let responseData = response.data;
      // Check if response.data is a string or an array
      if (typeof responseData === "string" || Array.isArray(responseData)) {
        return { data: responseData, status: response.status };
      }
      return { ...responseData, status: response.status };
    })
    .catch((err) => err.response);
}

export async function del(url, config = {}) {
  return await axiosApi
    .delete(url, { ...config })
    .then((response) => ({ ...response.data, status: response.status }));
}

export default axiosApi;
